import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import { getFileUrl } from "./fileUtils";
import defaultLogo from "../assets/utils/images/logo.svg";

const LEDGER_TYPES = {
  doctor: { amountField: "doctor_actual_amount", label: "Service Provider Ledger" },
  pharmacy: { amountField: "pharmacy_actual_amount", label: "Pharmacy Ledger" },
  patient: { amountField: "total_amount", label: "Patient Ledger" },
  sara: { amountField: "sara_share", label: "SARA Ledger" },
};

const PDF_WIDTH = 794;
const COLORS = {
  primary: "#3ac47d",
  primaryDark: "#2d9a63",
  navy: "#1e3a5f",
  credit: "#10b981",
  creditBg: "#ecfdf5",
  debit: "#ef4444",
  debitBg: "#fef2f2",
  balance: "#0f766e",
  balanceBg: "#f0fdfa",
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  rowAlt: "#f9fafb",
};

const getBrandingInfo = () => ({
  systemName: localStorage.getItem("system_name") || "SARA",
  logoSrc: getFileUrl(localStorage.getItem("system_logo")) || defaultLogo,
});

const loadImageForPdf = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve("");
      return;
    }
    if (src.startsWith("data:")) {
      resolve(src);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(src);
      }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });

const getAmount = (row, ledgerType) => {
  if (ledgerType === "sara") {
    return row.sara_share ?? row.doctor_ladger_details?.sara_share ?? 0;
  }
  const field = LEDGER_TYPES[ledgerType]?.amountField || "doctor_actual_amount";
  return row[field] ?? 0;
};

const getDescription = (row, ledgerType) => {
  if (ledgerType === "sara") {
    return row.required_action || row.doctor_ladger_details?.required_action || row.from || "-";
  }
  return row.required_action || "-";
};

const getLadgerAction = (row, ledgerType) => {
  if (ledgerType === "sara") {
    return row.ladger_action || row.doctor_ladger_details?.ladger_action || "";
  }
  return row.ladger_action || "";
};

const getStatus = (row, ledgerType) => {
  if (ledgerType === "sara") {
    return row.status ?? row.doctor_ladger_details?.status;
  }
  return row.status;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const normalizeSaraLedgerRows = (rows) =>
  (rows || []).map((row) => ({
    datetime_added: row.datetime_added,
    required_action: getDescription(row, "sara"),
    ladger_action: getLadgerAction(row, "sara"),
    sara_share: getAmount(row, "sara"),
    status: getStatus(row, "sara"),
  }));

export const calculateLedgerTotals = (data, ledgerType = "doctor") => {
  const credit = (data || [])
    .filter((row) => getLadgerAction(row, ledgerType) === "+")
    .reduce((sum, row) => sum + getAmount(row, ledgerType), 0);

  const debit = (data || [])
    .filter(
      (row) =>
        getLadgerAction(row, ledgerType) === "-" &&
        getStatus(row, ledgerType) !== "Rejected"
    )
    .reduce((sum, row) => sum + getAmount(row, ledgerType), 0);

  return { credit, debit, balance: credit - debit };
};

const createTransactionRow = (row, index, ledgerType, t) => {
  const amount = getAmount(row, ledgerType);
  const ladgerAction = getLadgerAction(row, ledgerType);
  const status = getStatus(row, ledgerType);
  const rowBg = index % 2 === 0 ? "#ffffff" : COLORS.rowAlt;

  let creditCell = `<span style="color:${COLORS.muted}">—</span>`;
  let debitCell = `<span style="color:${COLORS.muted}">—</span>`;
  let typeBadge = "";

  if (ladgerAction === "+") {
    creditCell = `<span style="color:${COLORS.credit};font-weight:700">+ ${amount.toFixed(2)}</span>`;
    typeBadge = `<span style="display:inline-block;background:${COLORS.creditBg};color:${COLORS.credit};font-size:9px;font-weight:700;padding:2px 8px;border-radius:10px;">CR</span>`;
  } else if (ladgerAction === "-" && status !== "Rejected") {
    debitCell = `<span style="color:${COLORS.debit};font-weight:700">- ${amount.toFixed(2)}</span>`;
    typeBadge = `<span style="display:inline-block;background:${COLORS.debitBg};color:${COLORS.debit};font-size:9px;font-weight:700;padding:2px 8px;border-radius:10px;">DR</span>`;
  } else if (ladgerAction === "-" && status === "Rejected") {
    debitCell = `<span style="color:${COLORS.muted};font-style:italic;font-size:10px">${t("Rejected")}</span>`;
    typeBadge = `<span style="display:inline-block;background:#f3f4f6;color:${COLORS.muted};font-size:9px;font-weight:700;padding:2px 8px;border-radius:10px;">—</span>`;
  }

  return `
    <tr style="background:${rowBg}">
      <td style="padding:11px 10px;border-bottom:1px solid ${COLORS.border};font-size:11px;color:${COLORS.muted};text-align:center;">${String(index + 1).padStart(2, "0")}</td>
      <td style="padding:11px 10px;border-bottom:1px solid ${COLORS.border};font-size:11px;color:${COLORS.text};white-space:nowrap;">
        <div style="font-weight:600">${moment(row.datetime_added).format("DD MMM YYYY")}</div>
        <div style="font-size:9px;color:${COLORS.muted};margin-top:2px">${moment(row.datetime_added).format("hh:mm A")}</div>
      </td>
      <td style="padding:11px 10px;border-bottom:1px solid ${COLORS.border};font-size:11px;color:${COLORS.text};line-height:1.45;">
        ${escapeHtml(getDescription(row, ledgerType))}
      </td>
      <td style="padding:11px 10px;border-bottom:1px solid ${COLORS.border};text-align:center;">${typeBadge}</td>
      <td style="padding:11px 10px;border-bottom:1px solid ${COLORS.border};text-align:right;font-size:11px;">${creditCell}</td>
      <td style="padding:11px 10px;border-bottom:1px solid ${COLORS.border};text-align:right;font-size:11px;">${debitCell}</td>
    </tr>
  `;
};

const buildPdfContent = async ({ data, entityName, ledgerType, totals, t }) => {
  const { systemName, logoSrc } = getBrandingInfo();
  const logoDataUrl = await loadImageForPdf(logoSrc);
  const ledgerLabel = t(LEDGER_TYPES[ledgerType]?.label || "Ledger");
  const statementId = `LGR-${moment().format("YYYYMMDD")}-${String(data.length).padStart(4, "0")}`;
  const generatedAt = moment().format("DD MMMM YYYY, hh:mm A");
  const statementPeriod =
    data.length > 0
      ? `${moment(data[data.length - 1]?.datetime_added).format("DD MMM YYYY")} → ${moment(data[0]?.datetime_added).format("DD MMM YYYY")}`
      : "—";
  const balanceColor = totals.balance >= 0 ? COLORS.credit : COLORS.debit;
  const balanceBg = totals.balance >= 0 ? COLORS.creditBg : COLORS.debitBg;

  const container = document.createElement("div");
  container.style.cssText = `
    width:${PDF_WIDTH}px;
    max-width:${PDF_WIDTH}px;
    margin:0 auto;
    background:#f3f4f6;
    font-family:'Segoe UI',Arial,Helvetica,sans-serif;
    color:${COLORS.text};
    box-sizing:border-box;
    padding:0;
  `;

  container.innerHTML = `
    <!-- Top banner -->
    <div style="background:linear-gradient(135deg,${COLORS.navy} 0%,#0f4c5c 55%,${COLORS.primaryDark} 100%);padding:28px 32px 24px;color:#fff;position:relative;overflow:hidden;">
      <div style="position:absolute;top:-40px;right:-40px;width:180px;height:180px;background:rgba(255,255,255,0.06);border-radius:50%;"></div>
      <div style="position:absolute;bottom:-60px;left:30%;width:240px;height:120px;background:rgba(58,196,125,0.15);border-radius:50%;"></div>

      <div style="display:flex;justify-content:space-between;align-items:flex-start;position:relative;z-index:1;">
        <div style="flex:1;">
          <div style="display:inline-block;background:rgba(58,196,125,0.25);border:1px solid rgba(58,196,125,0.5);color:#a7f3d0;font-size:9px;font-weight:800;letter-spacing:1.5px;padding:4px 12px;border-radius:20px;margin-bottom:12px;">
            ${ledgerLabel.toUpperCase()}
          </div>
          <div style="font-size:28px;font-weight:800;letter-spacing:-0.5px;line-height:1.1;margin-bottom:6px;">
            ${t("Ledger Statement")}
          </div>
          <div style="font-size:15px;font-weight:600;color:rgba(255,255,255,0.85);margin-bottom:4px;">
            ${escapeHtml(entityName)}
          </div>
          <div style="font-size:11px;color:rgba(255,255,255,0.55);">${escapeHtml(systemName)} Platform</div>
        </div>
        <div style="text-align:right;">
          ${
            logoDataUrl
              ? `<img src="${logoDataUrl}" alt="${escapeHtml(systemName)}" style="max-width:120px;max-height:48px;width:auto;height:auto;display:block;margin-left:auto;margin-bottom:8px;filter:brightness(0) invert(1);opacity:0.9;" />`
              : ""
          }
          <div style="font-size:10px;color:rgba(255,255,255,0.6);margin-top:4px;">${t("Statement ID")}</div>
          <div style="font-size:12px;font-weight:700;color:#fff;">${statementId}</div>
        </div>
      </div>
    </div>

    <!-- Meta strip -->
    <div style="background:#fff;border-bottom:1px solid ${COLORS.border};padding:14px 32px;display:flex;gap:0;">
      ${[
        { label: t("Statement Period"), value: statementPeriod },
        { label: t("Generated on"), value: generatedAt },
        { label: t("Transactions"), value: data.length },
      ]
        .map(
          (item, i, arr) => `
        <div style="flex:1;${i < arr.length - 1 ? `border-right:1px solid ${COLORS.border};` : ""}padding-right:16px;margin-right:16px;">
          <div style="font-size:9px;font-weight:700;color:${COLORS.muted};text-transform:uppercase;letter-spacing:0.8px;margin-bottom:3px;">${item.label}</div>
          <div style="font-size:12px;font-weight:600;color:${COLORS.text};">${escapeHtml(String(item.value))}</div>
        </div>`
        )
        .join("")}
    </div>

    <!-- Summary cards -->
    <div style="padding:20px 32px 8px;display:flex;gap:14px;">
      <div style="flex:1;background:#fff;border-radius:12px;padding:16px 18px;border:1px solid ${COLORS.border};box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <div style="font-size:9px;font-weight:700;color:${COLORS.muted};text-transform:uppercase;letter-spacing:0.8px;margin-bottom:8px;">${t("Total Credit")}</div>
        <div style="font-size:22px;font-weight:800;color:${COLORS.credit};line-height:1;">RM ${totals.credit.toFixed(2)}</div>
        <div style="font-size:10px;color:${COLORS.muted};margin-top:6px;">↑ ${t("Money In")}</div>
      </div>
      <div style="flex:1;background:#fff;border-radius:12px;padding:16px 18px;border:1px solid ${COLORS.border};box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <div style="font-size:9px;font-weight:700;color:${COLORS.muted};text-transform:uppercase;letter-spacing:0.8px;margin-bottom:8px;">${t("Total Debit")}</div>
        <div style="font-size:22px;font-weight:800;color:${COLORS.debit};line-height:1;">RM ${totals.debit.toFixed(2)}</div>
        <div style="font-size:10px;color:${COLORS.muted};margin-top:6px;">↓ ${t("Money Out")}</div>
      </div>
      <div style="flex:1;background:${balanceBg};border-radius:12px;padding:16px 18px;border:2px solid ${balanceColor};box-shadow:0 2px 8px rgba(0,0,0,0.08);">
        <div style="font-size:9px;font-weight:700;color:${COLORS.muted};text-transform:uppercase;letter-spacing:0.8px;margin-bottom:8px;">${t("Net Balance")}</div>
        <div style="font-size:22px;font-weight:800;color:${balanceColor};line-height:1;">RM ${totals.balance.toFixed(2)}</div>
        <div style="font-size:10px;color:${COLORS.muted};margin-top:6px;">= ${t("Closing Balance")}</div>
      </div>
    </div>

    <!-- Transaction table -->
    <div style="padding:12px 32px 24px;">
      <div style="font-size:11px;font-weight:700;color:${COLORS.muted};text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;padding-left:2px;">
        ${t("Transaction History")}
      </div>
      <div style="background:#fff;border-radius:12px;border:1px solid ${COLORS.border};overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,0.07);">
        <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
          <thead>
            <tr style="background:${COLORS.navy};color:#fff;">
              <th style="width:5%;padding:12px 8px;font-size:9px;font-weight:700;text-align:center;letter-spacing:0.5px;">#</th>
              <th style="width:15%;padding:12px 8px;font-size:9px;font-weight:700;text-align:left;letter-spacing:0.5px;">${t("Date")}</th>
              <th style="width:38%;padding:12px 8px;font-size:9px;font-weight:700;text-align:left;letter-spacing:0.5px;">${t("Description")}</th>
              <th style="width:8%;padding:12px 8px;font-size:9px;font-weight:700;text-align:center;letter-spacing:0.5px;">${t("Type")}</th>
              <th style="width:17%;padding:12px 10px;font-size:9px;font-weight:700;text-align:right;letter-spacing:0.5px;">${t("Credit (RM)")}</th>
              <th style="width:17%;padding:12px 10px;font-size:9px;font-weight:700;text-align:right;letter-spacing:0.5px;">${t("Debit (RM)")}</th>
            </tr>
          </thead>
          <tbody>
            ${data.map((row, index) => createTransactionRow(row, index, ledgerType, t)).join("")}
          </tbody>
          <tfoot>
            <tr style="background:#f0fdf4;border-top:2px solid ${COLORS.primary};">
              <td colspan="4" style="padding:13px 10px;font-size:12px;font-weight:800;color:${COLORS.text};text-align:right;">${t("Totals")}</td>
              <td style="padding:13px 10px;font-size:12px;font-weight:800;color:${COLORS.credit};text-align:right;">RM ${totals.credit.toFixed(2)}</td>
              <td style="padding:13px 10px;font-size:12px;font-weight:800;color:${COLORS.debit};text-align:right;">RM ${totals.debit.toFixed(2)}</td>
            </tr>
            <tr style="background:${balanceBg};">
              <td colspan="4" style="padding:13px 10px;font-size:13px;font-weight:800;color:${COLORS.text};text-align:right;">${t("Net Balance")}</td>
              <td colspan="2" style="padding:13px 10px;font-size:14px;font-weight:800;color:${balanceColor};text-align:right;">RM ${totals.balance.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Footer -->
    <div style="background:#fff;border-top:1px solid ${COLORS.border};padding:14px 32px;display:flex;justify-content:space-between;align-items:center;">
      <div style="font-size:9px;color:${COLORS.muted};line-height:1.5;">
        ${t("Computer-generated ledger statement")} · ${escapeHtml(systemName)}<br/>
        ${t("For financial record purposes only")}
      </div>
      <div style="font-size:9px;color:${COLORS.muted};text-align:right;">
        ${statementId}<br/>
        ${generatedAt}
      </div>
    </div>
  `;

  return container;
};

export const downloadLedgerPdf = async ({
  data,
  entityName,
  ledgerType = "doctor",
  fileName,
  t = (key) => key,
}) => {
  if (!data || data.length === 0) {
    throw new Error(t("No transaction data available."));
  }

  const totals = calculateLedgerTotals(data, ledgerType);
  const container = await buildPdfContent({ data, entityName, ledgerType, totals, t });

  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0";
  document.body.appendChild(container);

  try {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#f3f4f6",
      scrollY: -window.scrollY,
      width: PDF_WIDTH,
      height: container.scrollHeight + 20,
      windowWidth: PDF_WIDTH,
      windowHeight: container.scrollHeight + 20,
      x: 0,
      y: 0,
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png", 0.98);
    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const imgWidth = pdfWidth - 2 * margin;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight - 2 * margin;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 2 * margin;
    }

    const safeName = (entityName || "Ledger").replace(/[^a-zA-Z0-9_-]/g, "_");
    pdf.save(fileName || `${safeName}_Ledger_${moment().format("YYYY-MM-DD")}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
};
